import { optimizelyClient } from '@frontend/contexts/optimizelyClient';
import { useOpalUser, useOperations } from '@frontend/hooks';
import { InstanceOperationsName } from '@frontend/types';

/**
 * Component to check if instance has capabilities OR old feature flags enabled.
 *
 * This enables smooth migration from old feature flags to new capability-based guards.
 * Before FBP release: only old feature flags control access (capability check returns false via fallback)
 * After FBP release: either old flags OR new capability grants access (graceful migration)
 *
 * @param capabilities - instance capabilities to check (OR logic)
 * @param featureFlags - old feature flag names to check (OR logic). If empty, uses fallback=true for new guards.
 * @param children - children components to render if access granted
 * @param fallback - element to render when access is denied (defaults to null)
 * @returns JSX.Element - children if access granted, otherwise fallback element
 *
 * Note: This component is temporary during FBP migration.
 * TODO: Remove after FBP release and old flag cleanup. Use CanInstance instead.
 */
function CapabilityOrFlagGuard({
  capabilities,
  featureFlags,
  children,
  fallback = null,
}: {
  capabilities: InstanceOperationsName[];
  featureFlags: string[];
  children: JSX.Element;
  fallback?: JSX.Element | null;
}) {
  const { canInstance } = useOperations();
  const { opalUser } = useOpalUser();

  // If no feature flags provided, this is a new guard (use optimistic fallback=true)
  // Otherwise, migration guard (use fallback=false)
  const capabilityFallback = featureFlags.length === 0;

  // Check new capability
  const hasCapability = canInstance(capabilities, capabilityFallback);

  // Check each old feature flag (if any) using optimizelyClient imperatively
  const hasOldFlag = featureFlags.some(flagName => {
    return optimizelyClient.isFeatureEnabled(flagName, opalUser?.id || '', {
      email: opalUser?.email,
      instanceId: opalUser?.instance?.instance_id,
      customerId: opalUser?.instance?.customer_id,
      productSku: opalUser?.instance?.product_sku,
      productId: opalUser?.instance?.product_id,
    });
  });

  // Grant access if either capability OR old flag is enabled
  if (!hasCapability && !hasOldFlag) {
    return fallback;
  }

  return children;
}

export default CapabilityOrFlagGuard;
